import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, MessageCircle, Bell, Clock, MapPin } from "lucide-react";
import useAppointmentActions from "@/hooks/useAppointmentActions";
import { formatDateES, todayISO, appointmentRouteLabel } from "@/lib/utils-app";

function TodayCard({ appt, onWhatsapp, onReminder }) {
  return (
    <div
      data-testid={`today-card-${appt.id}`}
      className="border border-neutral-200 rounded-xl p-4 flex items-start justify-between gap-3"
    >
      <div className="min-w-0">
        <div className="font-semibold">{appt.student?.name || "—"}</div>
        <div className="text-sm text-neutral-500 mt-1 space-y-0.5">
          <div className="flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" /> {appt.time}
          </div>
          <div className="flex items-center gap-1.5" data-testid={`today-route-${appt.id}`}>
            <MapPin className="h-3.5 w-3.5" /> {appointmentRouteLabel(appt)}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2 items-end">
        <button
          onClick={onWhatsapp}
          data-testid={`today-whatsapp-${appt.id}`}
          className="btn-whatsapp !h-10 !px-3"
        >
          <MessageCircle className="h-4 w-4" />
          WhatsApp
        </button>
        <button
          onClick={onReminder}
          data-testid={`today-reminder-${appt.id}`}
          className="btn-secondary !h-10 !px-3"
        >
          <Bell className="h-4 w-4" />
          Recordar
        </button>
      </div>
    </div>
  );
}

export default function TodayDialog({ open, onOpenChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/appointments", { params: { today: true } });
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const actions = useAppointmentActions(load);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="today-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-orange-500" />
            Citas de hoy · {formatDateES(todayISO())}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-6 text-center text-neutral-500">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-neutral-500">
            No hay citas para hoy.
          </div>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {items.map((a) => (
              <TodayCard
                key={a.id}
                appt={a}
                onWhatsapp={() => actions.sendWhatsapp(a)}
                onReminder={() => actions.sendReminder(a)}
              />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
